import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty, isNil } from 'lodash';

import MessengerConversations from 'modules/messenger/components/MessengerConversations';
import Messages from './Messages';
import MessengerBrandBlankState from 'modules/messenger/components/MessengerBrandBlankState';

import { fetchConversations, fetchPreSelectedConversation, markConversationAsUnread, resetConversations, selectedConversationLoaded } from 'modules/messenger/reducers/conversations';
import { fetchUnreadConversations } from 'modules/messenger/reducers/messengerNotifications';
import { fetchSubcampaign, resetSubcampaign } from 'modules/subcampaigns/reducers/subcampaign';
import { fetchCampaignOverview } from 'modules/campaigns/reducers/campaignOverview';
import { fetchVariablePayments, resetVariablePayments } from 'modules/campaigns/reducers/variable-payments/variable-payments';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { isDisableUnreadMessagesSelector } from 'selectors/disableUnreadMessages';

import { getVariablePaymentsById } from 'utils/payouts';

import type {
  Action,
  Campaign,
  Dispatch,
  MessengerConversation,
  VariablePayments
} from 'types';

type State = {
  reseted: boolean,
  subcampaignId: number | undefined | null
};

type OwnProps = {
  subcampaignId: number,
  campaignId: number,
  externalConversationId?: string
};

type StateProps = {
  campaign: Campaign,
  currentOrganizationId: number | undefined | null,
  conversations: Array<MessengerConversation>,
  variablePayments: VariablePayments,
  isLoading: boolean,
  isFilterApplied: boolean,
  selectedConversation: MessengerConversation,
  disableUnreadMessages: boolean
};

type DispatchProps = {
  dispatch: Dispatch,
  dispatchFetchVariablePayments: (campaignId: number) => void,
  dispatchResetVariablePayments: () => void,
  dispatchFetchUnreadConversations: (campaignId: number) => Promise<void>,
  dispatchFetchConversations: (campaignId: number, page: number) => Promise<void>,
  dispatchResetConversations: () => Promise<void>,
  dispatchResetSubcampaign: () => Promise<void>,
  dispatchFetchSubcampaign: (subcampaignId: number) => Promise<void>,
  dispatchFetchCampaignOverview: (campaignId: number) => Promise<void>,
  dispatchMarkConversationAsUnread: (conversationExternalId: string) => Promise<void>,
  dispatchFetchPreSelectedConversation: (campaignId: number, subcampaignId?: number, conversationExternalId?: string) => Promise<void>,
  dispatchSelectedConversationLoaded: (conversation: MessengerConversation | null) => Action
};

type Props = OwnProps & StateProps & DispatchProps;

class MessengerBrand extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      reseted: false,
      subcampaignId: props.subcampaignId || null
    };
  }

  componentDidMount(): void {
    const {
      currentOrganizationId,
      disableUnreadMessages,
      campaignId,
      dispatchFetchConversations,
      dispatchFetchUnreadConversations,
      dispatchFetchSubcampaign,
      dispatchFetchVariablePayments,
      dispatchFetchPreSelectedConversation
    } = this.props;
    const { subcampaignId } = this.state;

    if (!disableUnreadMessages && currentOrganizationId) {
      dispatchFetchUnreadConversations(currentOrganizationId);
    }

    if (campaignId) {
      dispatchFetchConversations(campaignId, 1);
      dispatchFetchVariablePayments(campaignId);
    }

    if (subcampaignId) {
      dispatchFetchSubcampaign(subcampaignId);
      dispatchFetchPreSelectedConversation(campaignId, subcampaignId);
    }
  }

  componentDidUpdate(prevProps: Props): void {
    const {
      campaignId,
      subcampaignId,
      currentOrganizationId,
      disableUnreadMessages,
      externalConversationId,
      dispatchResetConversations,
      dispatchFetchConversations,
      dispatchFetchUnreadConversations,
      dispatchFetchSubcampaign,
      dispatchFetchVariablePayments,
      dispatchFetchPreSelectedConversation
    } = this.props;
    const { reseted } = this.state;
    const newExternalConversationId =
      externalConversationId !== prevProps.externalConversationId;

    if (!disableUnreadMessages && currentOrganizationId && prevProps.currentOrganizationId !== currentOrganizationId) {
      dispatchFetchUnreadConversations(currentOrganizationId);
    }

    if (campaignId && prevProps.campaignId !== campaignId) {
      // full reset on campaign change
      dispatchResetConversations();
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        subcampaignId: this.props.subcampaignId || null
      });

      dispatchFetchConversations(campaignId, 1);
      dispatchFetchVariablePayments(campaignId);
    }

    if (subcampaignId && prevProps.subcampaignId !== subcampaignId) {
      dispatchFetchSubcampaign(subcampaignId);
      dispatchFetchPreSelectedConversation(campaignId, subcampaignId);
    }

    if (
      (!isNil(externalConversationId) && !reseted)
      && (isNil(this.state.subcampaignId) || newExternalConversationId)
    ) {
      this.updateSubcampaignIdByConversationId(externalConversationId);
      dispatchFetchPreSelectedConversation(campaignId, undefined, externalConversationId);
    }
  }

  componentWillUnmount(): void {
    const { dispatchResetConversations, dispatchResetSubcampaign, dispatchResetVariablePayments } = this.props;

    dispatchResetConversations();
    dispatchResetSubcampaign();
    dispatchResetVariablePayments();
  }

  onSelectConversation = (conversation: MessengerConversation): void => {
    const {
      dispatchFetchSubcampaign,
      dispatchFetchCampaignOverview,
      dispatchSelectedConversationLoaded
    } = this.props;
    const { subcampaignId } = this.state;

    if (conversation.subcampaignId !== subcampaignId) {
      dispatchFetchSubcampaign(conversation.subcampaignId);
      dispatchSelectedConversationLoaded(conversation);

      if (conversation.subcampaign.campaign) {
        dispatchFetchCampaignOverview(conversation.subcampaign.campaign.id);
      }
    }

    this.setState({
      subcampaignId: conversation.subcampaignId
    });
  };

  handleSelectConversation = (conversation: MessengerConversation) => {
    const { dispatch } = this.props;
    const prompt: HTMLElement | null = document.querySelector('.js-prompt-exit-panel');

    if (prompt && prompt.dataset.message) {
      // ask the user
      dispatch(showConfirmModal({
        title: decodeURIComponent(prompt.dataset.title ?? ''),
        message: decodeURIComponent(prompt.dataset.message),
        onConfirm: () => { // change the conversation
          dispatch(hideConfirmModal());
          this.onSelectConversation(conversation);
        },
        // keep showing the current conversation
        onCancel: () => {
          dispatch(hideConfirmModal());
        }
      }));
    } else {
      // change the conversation (default behavior)
      this.onSelectConversation(conversation);
    }
  };

  handleMarkConversationAsUnread = (conversation: MessengerConversation): void => {
    const {
      currentOrganizationId,
      disableUnreadMessages,
      dispatchMarkConversationAsUnread,
      dispatchFetchUnreadConversations,
      dispatchSelectedConversationLoaded } = this.props;

    this.setState({ subcampaignId: null, reseted: true },
      () => {
        dispatchSelectedConversationLoaded(null);
        dispatchMarkConversationAsUnread(conversation.externalId)
          .then(() => {
            if (!disableUnreadMessages && currentOrganizationId) {
              dispatchFetchUnreadConversations(currentOrganizationId);
            }
          });
      }
    );
  };

  getVariablePaymentsSumByInfluencerId(subcampaignId?: number | null) {
    const { variablePayments } = this.props;
    const updatedVariablePayments = variablePayments.items || [];

    if (!subcampaignId || isEmpty(updatedVariablePayments)) {
      return 0;
    }

    return getVariablePaymentsById('subcampaignId', subcampaignId, updatedVariablePayments).reduce((res, item) => res + (item.amount || 0), 0);
  }

  updateSubcampaignIdByConversationId(id: string): void {
    const { conversations, dispatchFetchSubcampaign } = this.props;

    if (isNil(conversations) || conversations.length === 0) {
      return;
    }

    const subcampaignId = conversations.filter(
      conv => conv.externalId === id
    )[0]?.subcampaignId ?? null;

    if (subcampaignId === null) {
      return;
    }

    dispatchFetchSubcampaign(subcampaignId);

    this.setState({ subcampaignId });
  }

  renderMessenger = (): JSX.Element => {
    const { campaignId, selectedConversation } = this.props;
    const { subcampaignId } = this.state;
    const variablePaymentsAmount = this.getVariablePaymentsSumByInfluencerId(subcampaignId);

    return (
      <React.Fragment>
        <MessengerConversations
          subcampaignId={subcampaignId}
          onSelectConversation={this.handleSelectConversation}
          onMarkConversationAsUnread={this.handleMarkConversationAsUnread}
          campaignId={campaignId}
        />
        <Messages
          key={selectedConversation ? 'conversation-selected' : 'conversation-unselected'}
          subcampaignId={subcampaignId}
          selectedConversation={selectedConversation}
          variablePaymentsAmount={variablePaymentsAmount}
          type="brand"
        />
      </React.Fragment>
    );
  };

  render(): React.ReactNode {
    const { campaign, conversations, campaignId, isLoading, isFilterApplied } = this.props;

    return (
      <section className="messenger">
        {
          conversations.length === 0 && !isLoading && !isFilterApplied ? (
            <MessengerBrandBlankState
              campaignId={campaignId}
              isCcm={campaign?.campaignType === 'ccm'}
            />
          ) : (
            this.renderMessenger()
          )
        }
      </section>
    );
  }
}

const mapStateToProps = ({
  account,
  campaignBrief,
  campaignVariablePayments,
  conversations,
  organizations,
  runtimeConfig
}): StateProps => ({
  campaign: campaignBrief.brief,
  variablePayments: campaignVariablePayments.variablePayments,
  conversations: conversations.list,
  isLoading: conversations.isLoading,
  currentOrganizationId: organizations.currentOrganizationId,
  isFilterApplied: conversations.isFilterApplied,
  selectedConversation: conversations.selectedConversation,
  disableUnreadMessages: isDisableUnreadMessagesSelector({ runtimeConfig, account })
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatch,
  dispatchFetchVariablePayments: (campaignId: number): void => dispatch(fetchVariablePayments(campaignId)),
  dispatchResetVariablePayments: (): void => dispatch(resetVariablePayments()),
  dispatchMarkConversationAsUnread: (conversationExternalId) => dispatch(markConversationAsUnread(conversationExternalId)),
  dispatchFetchUnreadConversations: (organizationId) => dispatch(fetchUnreadConversations(organizationId)),
  dispatchFetchConversations: (campaignId: number, page: number) => dispatch(fetchConversations(campaignId, page, {}, true)),
  dispatchResetConversations: () => dispatch(resetConversations()),
  dispatchFetchCampaignOverview: (campaignId) => dispatch(fetchCampaignOverview(campaignId)),
  dispatchFetchSubcampaign: (subcampaignId) => dispatch(fetchSubcampaign(subcampaignId, { showLoading: false })),
  dispatchResetSubcampaign: () => dispatch(resetSubcampaign()),
  dispatchFetchPreSelectedConversation: (campaignId, subcampaignId?: number, externalConversationId?: string) =>
    dispatch(fetchPreSelectedConversation(campaignId, subcampaignId, externalConversationId)),
  dispatchSelectedConversationLoaded: (conversation: MessengerConversation | null) => dispatch(selectedConversationLoaded(conversation))
});

export default connect(mapStateToProps, mapDispatchToProps)(MessengerBrand);
